import React, { useEffect, useState } from 'react';
import {HashRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import Sidebar from './components/Sidebar';
import DashboardPage from './pages/DashboardPage';
import EventsPage from './pages/EventsPage';
import TemplatesPage from './pages/TemplatesPage';
import MapPage from './pages/MapPage';
import HistoryPage from './pages/HistoryPage';
import AssetsPage from './pages/AssetsPage';
import ActionPage from './pages/ActionPage';
import WorkflowPage from './pages/WorkflowPage';
import CreateEditWorkflowPage from './components/Workflow/CreateWorkflowPage';
import IncidentPage from './pages/IncidentPage';
import IncidentDetailPage from './components/Incident/IncidentDetailPage';
import AssetDetailPage from './components/Assets/AssetDetail';
import ViewWorkflowPage from './components/Workflow/ViewWorkflowPage';
import UserList from './components/User/UserList';
import AddOrEditUser from './components/User/AddOrEditUser';
import OrganizationList from './components/Organization/OrganizationList';
import AddOrganization from './components/Organization/AddOrganization';
import CreateEditEntity from './components/Entities/CreateEditEntity';
import EntityList from './components/Entities/List';
import Unauthorized from './Unauthorized';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RoleList from './components/Role/RoleList';
import AddRole from './components/Role/AddRole';
import { verifyToken } from './api';
import AppPage from './pages/AppPage';
import CreateNewApp from './components/App/CreateNewApp';
import { GlobalProvider } from './GlobalProvider';
import EntityView from './components/Entities/View';
import TriggerList from './components/Trigger/List';
import CreateEditTrigger from './components/Trigger/CreateEdit';
import TriggerActionList from './components/TriggerAction/TriggerActionList';
import TriggerActionCreate from './components/TriggerAction/TriggerActionCreate';
import AuditActionTable from './components/Audit/AuditActionTable';
import EntityIncident from './components/EntityIncident/EntityIncidentList'; // Import the new component
import ViewEntityDef from './components/Entities/ViewEntity';
import EntityIncidentView from './components/EntityIncident/EntityIncidentView'; // Import the new component
import BirdView from './components/BirdView/View';
import { IconProvider } from "./contexts/IconContext";
import AIAgentChat from './components/AIAgent/AgentView';
import CreateEditAIAgent from './components/AIAgent/CreateEditAgent';
import { createTheme, ThemeProvider } from '@mui/material';
import FlowChartPage from './components/FlowChart';
import MonitorList from './components/Monitor/List';
import CreateEditMonitor from './components/Monitor/CreateEdit';
import OvertureMap from './components/OvertureMap/index';
import Header from './components/Header/Header';
import MapEditorPage from './pages/MapEditorPage';
import HireAIEmployees from './pages/AgentMarketplace';
import MyAIEmployees from './pages/AgentWorkforce';
import AgentDetails from './pages/AgentDetails';
import AIEmployeeWorkspace from './pages/AIEmployeeWorkspace';

const customTheme = createTheme({
  palette: {
    primary: {
      main: '#000000', // Black as the primary color
    },
    secondary: {
      main: '#555555', // Dark gray as the secondary color
    },
    text: {
      primary: '#000000', // Black for primary text
      secondary: '#555555', // Dark gray for secondary text
    },
    background: {
      default: '#f4f4f4', // Optional: Set background color
      paper: '#ffffff',
    },
  },
  typography: {
    fontSize: 14, // Smaller base font size
    h1: { fontSize: '2rem' },
    h2: { fontSize: '1.75rem' },
    h3: { fontSize: '1.5rem' },
    h4: { fontSize: '1.25rem' },
    h5: { fontSize: '1rem' },
    h6: { fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.75rem' },
  },
});


const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);


  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        navigate('login');
        return;
      }

      try {
        const response = await verifyToken(token);
        if (response.data.valid) {
          setIsAuthenticated(true);
          setAuth(response.data.decoded);
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          navigate('login');
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        navigate('login');
      }
    };

    checkAuthentication();
  }, [location, navigate]);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {isAuthenticated ? (
        <>
          {/* Sidebar */}
          <div
            style={{
              position: 'fixed',
              width: '240px',
              height: '100vh',
              top: 0,
              left: 0,
              zIndex: 1200, // Higher than header
              backgroundColor: 'white',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <Sidebar />
          </div>

          {/* Main content wrapper */}
          <div style={{ 
            marginLeft: '240px',
            flexGrow: 1,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <Header auth={auth} />

            {/* Main Content */}
            <div
              style={{
                flexGrow: 1,
                padding: '0px 16px',
                marginTop: '64px', // Height of the header
                overflowY: 'auto',
                backgroundColor: '#f4f4f4'
              }}
            >
              <Routes>
                <Route path="/" element={auth.currentApp.adminApp ? <AppPage /> : <OvertureMap />} />
                <Route path="/dashboard" element={auth.currentApp.adminApp ? <AppPage /> : <DashboardPage />} />
                <Route path="/incidents" element={<IncidentPage />} />
                <Route path="/agents" element={<AIAgentChat />} />
                <Route path="/agents/new" element={<CreateEditAIAgent />} />
                <Route path="/agents/edit/:id" element={<CreateEditAIAgent />} />
                <Route path="/incident/:id" element={<IncidentDetailPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/birdview" element={<OvertureMap />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/create-entity" element={<CreateEditEntity />} />
                <Route path="/edit-entity/:entityId" element={<CreateEditEntity />} />
                <Route path="/entities" element={<EntityList />} />
                <Route path="/view-entity/:id" element={<EntityView />} />
                <Route path="/view-entity-def/:id" element={<ViewEntityDef />} />
                <Route path="/assets" element={<AssetsPage />} />
                <Route path="/actions" element={<ActionPage />} />
                <Route path="/apps" element={<AppPage />} />
                <Route path="/triggers" element={<TriggerList />} />
                <Route path="/create-trigger" element={<CreateEditTrigger />} />

                <Route path="/monitors" element={<MonitorList />} />
                <Route path="/create-monitor" element={<CreateEditMonitor />} />
                <Route path="/triggeractions" element={<TriggerActionList />} />
                <Route path="/audit" element={<AuditActionTable />} />
                <Route path="/create-trigger-action" element={<TriggerActionCreate />} />
                <Route path="/edit-trigger/:id" element={<CreateEditTrigger />} />
                <Route path="/app-create" element={<CreateNewApp />} />
                <Route path="/app/:appId/edit" element={<CreateNewApp />} />
                <Route path="/workflow" element={<WorkflowPage />} />
                <Route path="/create-workflow" element={<CreateEditWorkflowPage />} />
                <Route path="/flowchart" element={<FlowChartPage />} />
                <Route path="/edit-workflow/:workflowId" element={<CreateEditWorkflowPage />} />
               
                <Route path="/asset/:type/:assetId" element={<AssetDetailPage />} />
                <Route path="/workflow/:id" element={<ViewWorkflowPage />} />
                <Route path="/entity-incident/:id" element={<EntityIncidentView />} />
                <Route path="/overturemaps" element={<OvertureMap />} />
                <Route path="/entity-incidents" element={<EntityIncident />} />
                <Route path="/map-editor" element={<MapEditorPage />} />
                <Route path='/agent-marketplace' element={<HireAIEmployees />} />
                <Route path='/agent-marketplace/:id' element={<AgentDetails />} />
                <Route path='/agent-workforce' element={<MyAIEmployees />} />
                <Route path='/agent-workspace/:agentId' element={<AIEmployeeWorkspace />} />
                <Route path='/ai-employee-workspace/:agentId' element={<AIEmployeeWorkspace />} />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute roles={['org_admin', 'system_admin']}>
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-user"
                  element={
                    <ProtectedRoute roles={['org_admin', 'system_admin']}>
                      <AddOrEditUser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/edit-user/:userId"
                  element={
                    <ProtectedRoute roles={['org_admin', 'system_admin']}>
                      <AddOrEditUser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations"
                  element={
                    <ProtectedRoute roles={['system_admin']}>
                      <OrganizationList />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/add-organization"
                  element={
                    <ProtectedRoute roles={['system_admin']}>
                      <AddOrganization />
                    </ProtectedRoute>
                  }
                />
                 <Route
                  path="/edit-organization/:id"
                  element={
                    <ProtectedRoute roles={['system_admin']}>
                      <AddOrganization />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <ProtectedRoute roles={['system_admin']}>
                      <RoleList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/add-role"
                  element={
                    <ProtectedRoute roles={['system_admin']}>
                      <AddRole />
                    </ProtectedRoute>
                  }
                />
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Routes>
            </div>
          </div>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          width="100%"
          bgcolor="#f4f4f4"
        >
          <Login />
        </Box>
      )}
    </div>
  );
};

const AppWrapper = () => (
  <ThemeProvider theme={customTheme}>
    <CssBaseline />
    <HashRouter>
      <GlobalProvider>
        <IconProvider>
          <App />
        </IconProvider>
      </GlobalProvider>
    </HashRouter>
  </ThemeProvider>
);

export default AppWrapper;
